import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';

import { ordersApi } from 'api';
import { EventName, OrderStatus } from 'enums';
import { Order } from 'types';
import { eventUtils } from 'utils';

type UseSelectingAlternativesInput = {
  orderId: string;
  order: Order | undefined;
  onSuccess: (data: Order) => void;
  onError: (error: any) => void;
};

export type UseSelectingAlternativesResult = {
  showAlternativeSumModal: boolean;
  isChangeAmountLoading: boolean;
  isSelectAlternativeTradeMethodLoading: boolean;
  onSelectAlternativeAmount: (amount: number) => void;
  onSelectAlternativeTradeMethod: () => void;
  onDismissAlternativeSumModal: () => void;
};

export const useSelectingAlternatives = ({
  orderId,
  order,
  onSuccess,
  onError,
}: UseSelectingAlternativesInput): UseSelectingAlternativesResult => {
  const [showAlternativeSumModal, setShowAlternativeSumModal] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the trigger value for which a timer is currently set, preventing duplicate timers
  // and race conditions when the background poll delivers the new trigger before the mutation response.
  const timerForTriggerRef = useRef<string | null>(null);

  const sciCustomerAlternativesTriggerAt =
    order?.sciCustomerAlternativesTriggerAt;

  const isRequisitesStatus = order?.status === OrderStatus.Requisites;

  const handleSuccess = useCallback(
    (data: Order) => {
      timerForTriggerRef.current = null;
      setShowAlternativeSumModal(false);
      onSuccess(data);
    },
    [onSuccess],
  );

  const handleChangeAmountError = useCallback(
    (error: any) => {
      eventUtils.trackException({
        message: `changeAmount failed. ${
          error?.response?.data?.message || error?.message || ''
        }`,
      });
      onError(error);
    },
    [onError],
  );

  const handleResetTradeMethodError = useCallback(
    (error: any) => {
      eventUtils.trackException({
        message: `resetTradeMethod failed. ${
          error?.response?.data?.message || error?.message || ''
        }`,
      });
      onError(error);
    },
    [onError],
  );

  const { mutate: changeAmount, isLoading: isChangeAmountLoading } =
    useMutation(ordersApi.changeAmount, {
      onSuccess: handleSuccess,
      onError: handleChangeAmountError,
    });

  const {
    mutate: resetTradeMethod,
    isLoading: isSelectAlternativeTradeMethodLoading,
  } = useMutation(ordersApi.resetTradeMethod, {
    onSuccess: handleSuccess,
    onError: handleResetTradeMethodError,
  });

  const onSelectAlternativeAmount = useCallback(
    (amount: number) => {
      eventUtils.trackEvent({
        event: EventName.SelectAlternativeAmount,
        message: String(amount),
      });
      setShowAlternativeSumModal(false);
      changeAmount({ id: orderId, amount });
    },
    [orderId, changeAmount],
  );

  const onSelectAlternativeTradeMethod = useCallback(() => {
    eventUtils.trackEvent({
      event: EventName.ResetTradeMethod,
    });
    setShowAlternativeSumModal(false);
    resetTradeMethod(orderId);
  }, [orderId, resetTradeMethod]);

  const onDismissAlternativeSumModal = useCallback(() => {
    eventUtils.trackEvent({ event: EventName.DismissAlternativesModal });
    setShowAlternativeSumModal(false);
  }, []);

  useEffect(() => {
    if (!isRequisitesStatus) {
      timerForTriggerRef.current = null;
      setShowAlternativeSumModal(false);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    if (
      !sciCustomerAlternativesTriggerAt ||
      timerForTriggerRef.current === sciCustomerAlternativesTriggerAt
    ) {
      return;
    }

    const delayMs = Math.max(
      0,
      new Date(sciCustomerAlternativesTriggerAt).getTime() - Date.now(),
    );
    timerForTriggerRef.current = sciCustomerAlternativesTriggerAt;
    timerRef.current = setTimeout(() => {
      eventUtils.trackEvent({
        event: EventName.ShowAlternativesModal,
      });
      setShowAlternativeSumModal(true);
    }, delayMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // Relinquish the claim on this trigger (rather than marking it "dismissed") so a
      // re-run — including React 18 Strict Mode's dev-only mount/cleanup/remount cycle,
      // which reuses the same refs — re-arms the timer instead of permanently skipping it.
      timerForTriggerRef.current = null;
    };
  }, [sciCustomerAlternativesTriggerAt, isRequisitesStatus]);

  return {
    showAlternativeSumModal,
    isChangeAmountLoading,
    isSelectAlternativeTradeMethodLoading,
    onSelectAlternativeAmount,
    onSelectAlternativeTradeMethod,
    onDismissAlternativeSumModal,
  };
};
